// UserManagement coordinator component

import { useAvailableTenants } from '@/api/hooks/useAvailableTenants'
import { useAvailableRoles, useUsersList } from '@/api/hooks/useUsers'
import {
  useCreateApiKeyMutation,
  useEmailPasswordResetLinkMutation,
} from '@/api/hooks/useUsers/mutations'
import { UpdateUserPayload } from '@/api/hooks/useUsers/transformers'
import type { User } from '@/api/types'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import { Button } from 'antd'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ActionsRenderer,
  ActiveSwitchRenderer,
  CreateApiKeyModal,
  CreateUserDrawer,
  DomainsRenderer,
  EmailVerifiedRenderer,
} from './components'
import { useGridManagement, useUserCRUD } from './hooks'
import { getColumnDefinitions, transformTenants } from './utils'

export function UserManagementPage() {
  const [searchText, setSearchText] = useState('')
  const [apiKeyUser, setApiKeyUser] = useState<User | null>(null)
  const createApiKeyMutation = useCreateApiKeyMutation()
  const emailPasswordResetLinkMutation = useEmailPasswordResetLinkMutation()

  const handleSendPasswordResetLink = useCallback(
    (user: User) => {
      if (!user.email) return
      emailPasswordResetLinkMutation.mutate({
        params: { query: { email: user.email } },
      })
    },
    [emailPasswordResetLinkMutation],
  )

  const { data: usersResponse, isLoading: userLoading } = useUsersList({
    search: searchText,
    limit: Number.MAX_SAFE_INTEGER,
  })

  const { data: availableRoles, isLoading: rolesLoading } = useAvailableRoles()

  const isLoading = userLoading || rolesLoading

  const users = usersResponse?.data || []

  const { gridRef, handleGridReady } = useGridManagement()

  const resetRowHeights = useCallback(() => {
    gridRef.current?.resetRowHeights()
  }, [gridRef])

  // Initialize CRUD operations
  const {
    isCreateDrawerOpen,
    openCreateDrawer,
    closeCreateDrawer,
    handleCreateSubmit,
    form,
    createMutation,
    updateMutation,
    handleToggleStatus,
  } = useUserCRUD(resetRowHeights)

  const { availableTenants } = useAvailableTenants()
  const transformedTenants = useMemo(
    () => transformTenants(availableTenants),
    [availableTenants],
  )

  useEffect(() => {
    resetRowHeights()
  }, [resetRowHeights, users])

  // Create enhanced domains renderer with handlers
  const ActiveSwitchRendererWithHandlers: React.FC<{ data: User }> = ({
    data,
  }) => <ActiveSwitchRenderer data={data} onToggle={handleToggleStatus} />

  const handleManageAccess = useCallback(
    (user: User, tenantName: string, roleIds: string[]) => {
      const updatedDomains = (user.domains || []).filter(
        (domain) => domain.tenant_name !== tenantName,
      )

      const grantedBy = user.email || 'system'

      updatedDomains.push({
        tenant_name: tenantName,
        granted_by: grantedBy,
        roles: roleIds,
        granted_on: new Date().toISOString(),
      })

      const payload: UpdateUserPayload = {
        ...user,
        domains: updatedDomains,
      }

      updateMutation.mutate(
        {
          body: payload,
          params: {
            header: { 'X-Tenant-Name': 'Gravitate' },
          },
        },
        {
          onSuccess: () => {
            resetRowHeights()
          },
        },
      )
    },
    [resetRowHeights, updateMutation],
  )

  // Create actions renderer with handlers
  const ActionsRendererWithHandlers: React.FC<{ data: User }> = ({ data }) => (
    <ActionsRenderer
      data={data}
      onUpdateAccess={handleManageAccess}
      onCreateApiKey={setApiKeyUser}
      onSendPasswordResetLink={handleSendPasswordResetLink}
      availableTenants={transformedTenants}
      availableRoles={availableRoles}
    />
  )

  // Generate column definitions
  const columnDefs = getColumnDefinitions(
    ActiveSwitchRendererWithHandlers,
    DomainsRenderer,
    EmailVerifiedRenderer,
    ActionsRendererWithHandlers,
  )

  const controlBarProps = useMemo(
    () => ({
      title: 'User Management',
      searchProps: {
        placeholder: 'Search users...',
        value: searchText,
        onChange: (e: ChangeEvent<HTMLInputElement>) =>
          setSearchText(e.target.value),
        prefix: <SearchOutlined />,
      },
      actionButtons: (
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={openCreateDrawer}
        >
          Create User
        </Button>
      ),
    }),
    [searchText, setSearchText, openCreateDrawer],
  )

  return (
    <>
      <Vertical bg='layout' style={{ height: '100%' }}>
        <GraviGrid
          rowData={users}
          columnDefs={columnDefs}
          loading={isLoading}
          controlBarProps={controlBarProps}
          updateEP={async (updatedUser) => {
            const payload = updatedUser as UpdateUserPayload
            updateMutation.mutate(
              {
                body: payload,
                params: { header: { 'X-Tenant-Name': 'Gravitate' } },
              },
              {
                onSuccess: () => {
                  resetRowHeights()
                },
              },
            )
          }}
          agPropOverrides={{
            context: { roles: availableRoles || [] },
            onGridReady: handleGridReady,
            getRowId: (params) => params.data.email,
            defaultColDef: {
              sortable: true,
              filter: true,
              resizable: true,
              editable: false,
              flex: 1,
              minWidth: 140,
              cellStyle: {
                whiteSpace: 'normal',
                textOverflow: 'clip',
              },
            },
            animateRows: true,
            domLayout: 'normal',
            pagination: false,
          }}
        />
      </Vertical>

      <CreateUserDrawer
        isOpen={isCreateDrawerOpen}
        onClose={closeCreateDrawer}
        onSubmit={handleCreateSubmit}
        form={form}
        availableRoles={availableRoles}
        isLoading={createMutation.isPending || updateMutation.isPending}
        tenantOptions={transformedTenants}
      />

      <CreateApiKeyModal
        isOpen={!!apiKeyUser}
        onClose={() => setApiKeyUser(null)}
        user={apiKeyUser}
        availableTenants={transformedTenants}
        onSubmit={(values) => {
          createApiKeyMutation.mutate(
            {
              body: values,
              params: { header: { 'X-Tenant-Name': 'Gravitate' } },
            },
            { onSuccess: () => setApiKeyUser(null) },
          )
        }}
        isLoading={createApiKeyMutation.isPending}
      />
    </>
  )
}
